import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useInvoice } from "../hooks/useInvoice";
import { InvoiceStatus, InvoiceType, Haulier, HAULIER_LABELS } from "../types";
import type { ZoneSummary as ZoneSummaryType, Invoice, FuelStatus } from "../types";
import ProcessingStatus from "../components/ProcessingStatus";
import InvoiceSummary from "../components/InvoiceSummary";
import LineItemsTable from "../components/LineItemsTable";
import { exportInvoiceCsv } from "../utils/export";
import { invoices, ApiError } from "../services/api";
import DiscrepancyReport from "../components/DiscrepancyReport";

// ─── Status badge colors ────────────────────────────────────────────────────

const STATUS_BADGE: Record<InvoiceStatus, string> = {
  [InvoiceStatus.PROCESSING]: "bg-blue-100 text-blue-800",
  [InvoiceStatus.VERIFIED]: "bg-green-100 text-green-800",
  [InvoiceStatus.ISSUES_FOUND]: "bg-red-100 text-red-800",
  [InvoiceStatus.ERROR]: "bg-gray-100 text-gray-800",
};

const TYPE_BADGE: Record<InvoiceType, { css: string; label: string }> = {
  [InvoiceType.HAULIER]: { css: "bg-indigo-100 text-indigo-800", label: "Haulier" },
  [InvoiceType.WAREHOUSE]: { css: "bg-teal-100 text-teal-800", label: "Warehouse" },
  [InvoiceType.SUNDRY]: { css: "bg-orange-100 text-orange-800", label: "VAT Invoice" },
  [InvoiceType.HAULIER_BULK]: { css: "bg-cyan-100 text-cyan-800", label: "Haulier - Bulk" },
  [InvoiceType.VAT_HAULAGE]: { css: "bg-orange-100 text-orange-800", label: "VAT Inv Haulage" },
  [InvoiceType.VAT_STORAGE]: { css: "bg-amber-100 text-amber-800", label: "VAT Inv Storage" },
  [InvoiceType.HAULIER_COMPANION]: { css: "bg-yellow-100 text-yellow-800", label: "Box PDF (awaiting CSV)" },
};

const FUEL_STATUS_CONFIG: Record<Exclude<FuelStatus, "NOT_APPLICABLE">, { css: string; label: string }> = {
  UNCONFIRMED: { css: "bg-amber-100 text-amber-800", label: "Fuel unconfirmed" },
  OVERCHARGED: { css: "bg-red-100 text-red-800", label: "Fuel overcharged" },
  UNDERCHARGED: { css: "bg-blue-100 text-blue-800", label: "Fuel undercharged" },
  CORRECT: { css: "bg-green-100 text-green-800", label: "Fuel correct" },
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError, error } = useInvoice(id);

  // ── Loading state ───────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <svg
          className="h-8 w-8 animate-spin text-blue-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        <span className="ml-3 text-gray-600">Loading invoice...</span>
      </div>
    );
  }

  // ── Error state ─────────────────────────────────────────────────────────
  if (isError || !data) {
    if (error instanceof ApiError && error.status === 404) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <h2 className="text-lg font-semibold text-red-800">
            This invoice record no longer exists
          </h2>
          <p className="mt-1 text-sm text-red-600">
            If you uploaded a Box Logistics PDF, it has been attached to the
            matching CSV invoice. Open the invoice list to find it.
          </p>
          <Link
            to="/invoices"
            className="mt-4 inline-block text-sm font-medium text-blue-600 hover:underline"
          >
            Go to invoices
          </Link>
        </div>
      );
    }

    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6">
        <h2 className="text-lg font-semibold text-red-800">
          Failed to load invoice
        </h2>
        <p className="mt-1 text-sm text-red-600">
          {error instanceof Error ? error.message : "Unknown error"}
        </p>
        <Link
          to="/dashboard"
          className="mt-4 inline-block text-sm font-medium text-blue-600 hover:underline"
        >
          Back to dashboard
        </Link>
      </div>
    );
  }

  const { invoice, haulier_lines = [], warehouse_lines = [] } = data;
  const isProcessing = invoice.status === InvoiceStatus.PROCESSING;
  const isHaulier = invoice.invoice_type === InvoiceType.HAULIER;
  const isSundry = [InvoiceType.SUNDRY, InvoiceType.HAULIER_BULK, InvoiceType.VAT_HAULAGE, InvoiceType.VAT_STORAGE].includes(invoice.invoice_type);
  const isBox = invoice.haulier === Haulier.BOX_LOGISTICS;

  const handleExport = () => {
    exportInvoiceCsv(invoice, haulier_lines, warehouse_lines);
  };

  const handleDownload = () => {
    invoices.downloadFile(invoice.id);
  };

  return (
    <div className="space-y-6">
      {/* ── Duplicate Warning Banner ──────────────────────────────────────── */}
      {invoice.duplicate_of && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 text-amber-600"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-sm font-medium text-amber-800">
              This invoice appears to be a duplicate of Invoice{" "}
              <Link
                to={`/invoice/${invoice.duplicate_of}`}
                className="font-semibold underline hover:text-amber-900"
              >
                #{invoice.duplicate_of}
              </Link>
            </p>
          </div>
        </div>
      )}

      {/* ── Header Panel ──────────────────────────────────────────────────── */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {invoice.invoice_number
                  ? [
                      invoice.supplier,
                      invoice.invoice_number,
                      invoice.invoice_date ? formatDate(invoice.invoice_date) : null,
                    ].filter(Boolean).join(" — ")
                  : invoice.status === InvoiceStatus.PROCESSING
                    ? "Processing Invoice…"
                    : "Invoice"}
              </h1>
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${TYPE_BADGE[invoice.invoice_type].css}`}
              >
                {TYPE_BADGE[invoice.invoice_type].label}
              </span>
              {invoice.invoice_type !== InvoiceType.WAREHOUSE && (
                <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                  {HAULIER_LABELS[invoice.haulier ?? Haulier.PALLETWAYS]}
                </span>
              )}
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[invoice.status]}`}
              >
                {invoice.status.replace(/_/g, " ")}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
              {invoice.supplier && (
                <span>
                  <span className="font-medium text-gray-500">Supplier:</span>{" "}
                  {invoice.supplier}
                </span>
              )}
              {invoice.invoice_date && (
                <span>
                  <span className="font-medium text-gray-500">
                    Invoice Date:
                  </span>{" "}
                  {formatDate(invoice.invoice_date)}
                </span>
              )}
              <span>
                <span className="font-medium text-gray-500">Uploaded by:</span>{" "}
                {invoice.uploaded_by}
              </span>
              <span>
                <span className="font-medium text-gray-500">
                  Upload Date:
                </span>{" "}
                {formatDate(invoice.uploaded_at)}
              </span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            {invoice.file_key && (
              <button
                onClick={handleDownload}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                  />
                </svg>
                Original File
              </button>
            )}
            {!isProcessing && (
              <button
                onClick={handleExport}
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                  />
                </svg>
                Export CSV
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Processing Status Panel ───────────────────────────────────────── */}
      {isProcessing && <ProcessingStatus invoice={invoice} />}

      {/* ── Not Processed Panel (ERROR status) ──────────────────────────────── */}
      {invoice.status === InvoiceStatus.ERROR && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-6">
          <h2 className="text-lg font-semibold text-gray-900">Not processed</h2>
          <p className="mt-2 text-sm text-gray-700">
            {invoice.error_message ??
              invoice.processing_step?.replace(/^Error:\s*/, "") ??
              "This file could not be processed."}
          </p>
        </div>
      )}

      {/* ── Summary Panel (when processing complete) ──────────────────────── */}
      {!isProcessing && invoice.summary && (
        <InvoiceSummary summary={invoice.summary} />
      )}

      {/* ── Fuel Surcharge & PDF Panel (Box Logistics only) ────────────────── */}
      {!isProcessing && isBox && <FuelSurchargePanel invoice={invoice} />}

      {/* ── Sundry Invoice Summary ────────────────────────────────────────── */}
      {!isProcessing && isSundry && (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            Invoice Summary
          </h2>

          {/* Sundry line items table */}
          {invoice.sundry_lines && invoice.sundry_lines.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                    <th className="px-3 py-2">Description</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Unit Price</th>
                    <th className="px-3 py-2 text-right">Net Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.sundry_lines.map((line, idx) => (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="px-3 py-2 text-gray-900">{line.description}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-900">{line.quantity}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-900">{formatCurrency(line.unit_price)}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-900">{formatCurrency(line.net_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Totals */}
          <div className="mt-4 border-t border-gray-200 pt-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Net Amount</p>
                <p className="mt-1 font-mono text-lg font-semibold text-gray-900">{formatCurrency(invoice.net_amount)}</p>
              </div>
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">VAT</p>
                <p className="mt-1 font-mono text-lg font-semibold text-gray-900">{formatCurrency(invoice.vat_amount)}</p>
              </div>
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Gross Amount</p>
                <p className="mt-1 font-mono text-lg font-semibold text-gray-900">{formatCurrency(invoice.gross_amount)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Warehouse Summary (warehouse only) ────────────────────────────── */}
      {!isProcessing && !isHaulier && !isSundry && invoice.warehouse_summary && (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            Warehouse Summary
          </h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <WarehouseSummaryCard
              label="RH&D Out"
              count={invoice.warehouse_summary.pick_count}
              total={invoice.warehouse_summary.pick_total}
            />
            <WarehouseSummaryCard
              label="RH&D In"
              count={invoice.warehouse_summary.receipt_count}
              total={invoice.warehouse_summary.receipt_total}
            />
            <WarehouseSummaryCard
              label="Storage"
              count={invoice.warehouse_summary.rental_count}
              total={invoice.warehouse_summary.rental_total}
            />
          </div>
        </div>
      )}

      {/* ── Financial Summary (haulier only) ──────────────────────────────── */}
      {!isProcessing && isHaulier && (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            Financial Summary
          </h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {/* Net */}
            <FinancialCard
              label="Net Amount"
              stated={invoice.net_amount}
              calculated={
                invoice.summary
                  ? haulier_lines.reduce(
                      (sum, l) => sum + l.expected_amount,
                      0,
                    )
                  : null
              }
            />
            {/* VAT */}
            <FinancialCard
              label="VAT (20%)"
              stated={invoice.vat_amount}
              calculated={invoice.net_amount * 0.2}
            />
            {/* Gross */}
            <FinancialCard
              label="Gross Amount"
              stated={invoice.gross_amount}
              calculated={invoice.net_amount + invoice.net_amount * 0.2}
            />
          </div>
        </div>
      )}

      {/* ── Returns Summary (haulier only) ──────────────────────────── */}
      {!isProcessing && isHaulier && invoice.returns_summary && invoice.returns_summary.count > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 text-amber-600"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M7.793 2.232a.75.75 0 01-.025 1.06L3.622 7.25h10.003a5.375 5.375 0 010 10.75H10.75a.75.75 0 010-1.5h2.875a3.875 3.875 0 000-7.75H3.622l4.146 3.957a.75.75 0 01-1.036 1.085l-5.5-5.25a.75.75 0 010-1.085l5.5-5.25a.75.75 0 011.06.025z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-sm font-semibold text-amber-800">
              Returns: {invoice.returns_summary.count} consignment{invoice.returns_summary.count !== 1 ? "s" : ""},{" "}
              {formatCurrency(invoice.returns_summary.total_cost)} total cost
            </span>
          </div>
        </div>
      )}

      {/* ── Zone Summary (haulier only) ──────────────────────────────── */}
      {!isProcessing && isHaulier && invoice.zone_summary && invoice.zone_summary.length > 0 && (
        <ZoneSummarySection zones={invoice.zone_summary} />
      )}

      {/* ── Discrepancy Report (when issues found) ─────────────────────── */}
      {!isProcessing && invoice.status === InvoiceStatus.ISSUES_FOUND && (
        <DiscrepancyReport
          invoice={invoice}
          haulierLines={haulier_lines}
          warehouseLines={warehouse_lines}
        />
      )}

      {/* ── Line Items Table ──────────────────────────────────────────────── */}
      {!isProcessing && (haulier_lines.length > 0 || warehouse_lines.length > 0) && (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">
            Line Items
          </h2>
          <LineItemsTable
            invoiceType={invoice.invoice_type}
            haulierLines={haulier_lines}
            warehouseLines={warehouse_lines}
            showReturns={isHaulier && haulier_lines.some((l) => l.is_return)}
          />
        </div>
      )}
    </div>
  );
}

// ─── Financial card sub-component ───────────────────────────────────────────

function WarehouseSummaryCard({
  label,
  count,
  total,
}: {
  label: string;
  count: number;
  total: number;
}) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <div className="mt-2 space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Lines</span>
          <span className="font-mono text-sm font-semibold text-gray-900">
            {count}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Total</span>
          <span className="font-mono text-sm font-semibold text-gray-900">
            {formatCurrency(total)}
          </span>
        </div>
      </div>
    </div>
  );
}

function ZoneSummarySection({ zones }: { zones: ZoneSummaryType[] }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <h2 className="mb-4 text-lg font-semibold text-gray-900">
        Zone Summary
      </h2>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              <th className="px-3 py-2">Zone</th>
              <th className="px-3 py-2">Lines</th>
              <th className="px-3 py-2 text-right">Invoiced</th>
              <th className="px-3 py-2 text-right">Expected</th>
              <th className="px-3 py-2 text-right">Difference</th>
            </tr>
          </thead>
          <tbody>
            {zones.map((zone) => (
              <ZoneRow key={zone.zone} zone={zone} />
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-300 font-semibold text-gray-900">
              <td className="px-3 py-2">Total</td>
              <td className="px-3 py-2">
                {zones.reduce((s, z) => s + z.lines, 0)}
              </td>
              <td className="px-3 py-2 text-right">
                {formatCurrency(zones.reduce((s, z) => s + z.total_invoiced, 0))}
              </td>
              <td className="px-3 py-2 text-right">
                {formatCurrency(zones.reduce((s, z) => s + z.total_expected, 0))}
              </td>
              <td className="px-3 py-2 text-right">
                {formatCurrency(zones.reduce((s, z) => s + z.difference, 0))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function ZoneRow({ zone }: { zone: ZoneSummaryType }) {
  const hasDiff = Math.abs(zone.difference) >= 0.01;

  return (
    <>
      <tr className="border-b border-gray-100">
        <td className="px-3 py-2 font-medium text-gray-900">
          Zone {zone.zone}
        </td>
        <td className="px-3 py-2 text-gray-600">{zone.lines}</td>
        <td className="px-3 py-2 text-right font-mono text-gray-900">
          {formatCurrency(zone.total_invoiced)}
        </td>
        <td className="px-3 py-2 text-right font-mono text-gray-900">
          {formatCurrency(zone.total_expected)}
        </td>
        <td
          className={`px-3 py-2 text-right font-mono font-semibold ${
            hasDiff ? "text-red-600" : "text-green-600"
          }`}
        >
          {hasDiff ? formatCurrency(zone.difference) : "Match"}
        </td>
      </tr>
      {zone.pallet_breakdown.map((pb) => (
        <tr
          key={`${zone.zone}-${pb.pallet_type}`}
          className="border-b border-gray-50 bg-gray-50/50 text-xs text-gray-500"
        >
          <td className="py-1.5 pl-8 pr-3">{pb.pallet_type}</td>
          <td className="px-3 py-1.5">{pb.count}</td>
          <td className="px-3 py-1.5 text-right font-mono" colSpan={2} />
          <td
            className={`px-3 py-1.5 text-right font-mono ${
              Math.abs(pb.per_unit_difference) >= 0.01 ? "text-red-500" : "text-green-500"
            }`}
          >
            {Math.abs(pb.per_unit_difference) >= 0.01
              ? `${formatCurrency(pb.per_unit_difference)}/pallet`
              : "Match"}
          </td>
        </tr>
      ))}
    </>
  );
}

function FinancialCard({
  label,
  stated,
  calculated,
}: {
  label: string;
  stated: number;
  calculated: number | null;
}) {
  const diff = calculated != null ? stated - calculated : null;
  const matches = diff != null && Math.abs(diff) < 0.01;

  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <div className="mt-2 space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Invoice stated</span>
          <span className="font-mono text-sm font-semibold text-gray-900">
            {formatCurrency(stated)}
          </span>
        </div>
        {calculated != null && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Calculated</span>
            <span className="font-mono text-sm font-semibold text-gray-900">
              {formatCurrency(calculated)}
            </span>
          </div>
        )}
        {diff != null && (
          <div className="flex items-center justify-between border-t border-gray-200 pt-1">
            <span className="text-sm text-gray-600">Difference</span>
            <span
              className={`font-mono text-sm font-semibold ${
                matches
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {matches ? "Match" : formatCurrency(diff)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Fuel Surcharge & PDF panel (Box Logistics) ─────────────────────────────

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function companionStatusLabel(status?: string): string {
  switch (status) {
    case "ATTACHED":
      return "PDF attached";
    case "AWAITING_PDF":
      return "Awaiting PDF";
    case "AWAITING_CSV":
      return "Awaiting CSV";
    default:
      return "—";
  }
}

function fuelStatusBadge(status?: string) {
  if (!status || status === "NOT_APPLICABLE") return null;
  const c = FUEL_STATUS_CONFIG[status as Exclude<FuelStatus, "NOT_APPLICABLE">];
  if (!c) return null;
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${c.css}`}>
      {c.label}
    </span>
  );
}

function FuelSurchargePanel({ invoice }: { invoice: Invoice }) {
  const queryClient = useQueryClient();
  const [dieselPence, setDieselPence] = useState<string | null>(null);

  const fuelCheck = useMutation({
    mutationFn: (pence: number) => invoices.fuelCheck(invoice.id, pence),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice", invoice.id] });
      setDieselPence(null);
    },
  });

  const prefill = invoice.fuel_diesel_pence ?? invoice.fuel_last_used?.diesel_pence ?? null;
  const value = dieselPence ?? (prefill != null ? String(prefill) : "");

  const handleCheck = () => {
    const pence = parseFloat(value);
    if (!Number.isFinite(pence)) return;
    fuelCheck.mutate(pence);
  };

  const showProvenance =
    dieselPence === null && invoice.fuel_diesel_pence == null && invoice.fuel_last_used != null;

  const hasPdfTotals = invoice.pdf_net_amount != null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          Fuel Surcharge &amp; PDF
        </h2>
        {fuelStatusBadge(invoice.fuel_status)}
      </div>

      <p className="text-sm text-gray-600">
        <span className="font-medium text-gray-500">Companion PDF:</span>{" "}
        {companionStatusLabel(invoice.companion_status)}
      </p>

      {invoice.companion_note && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
          <h3 className="text-sm font-semibold text-red-800">
            PDF and CSV totals disagree
          </h3>
          <p className="mt-1 text-sm text-red-700">{invoice.companion_note}</p>
        </div>
      )}

      {hasPdfTotals && (
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">PDF Net</p>
            <p className="mt-1 font-mono text-lg font-semibold text-gray-900">
              {formatCurrency(invoice.pdf_net_amount ?? 0)}
            </p>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">PDF VAT</p>
            <p className="mt-1 font-mono text-lg font-semibold text-gray-900">
              {formatCurrency(invoice.pdf_vat_amount ?? 0)}
            </p>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">PDF Gross</p>
            <p className="mt-1 font-mono text-lg font-semibold text-gray-900">
              {formatCurrency(invoice.pdf_gross_amount ?? 0)}
            </p>
          </div>
        </div>
      )}

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Billed Surcharge</p>
          <div className="mt-2 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Percentage</span>
              <span className="font-mono text-sm font-semibold text-gray-900">
                {invoice.fuel_surcharge_pct != null ? `${invoice.fuel_surcharge_pct}%` : "—"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Amount</span>
              <span className="font-mono text-sm font-semibold text-gray-900">
                {invoice.fuel_surcharge_amount != null ? formatCurrency(invoice.fuel_surcharge_amount) : "—"}
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Expected Surcharge</p>
          <div className="mt-2 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Percentage</span>
              <span className="font-mono text-sm font-semibold text-gray-900">
                {invoice.fuel_expected_pct != null ? `${invoice.fuel_expected_pct}%` : "—"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Amount</span>
              <span className="font-mono text-sm font-semibold text-gray-900">
                {invoice.fuel_expected_amount != null ? formatCurrency(invoice.fuel_expected_amount) : "—"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Diesel price this week (pence per litre, ex VAT)
          </label>
          <input
            type="number"
            step="0.1"
            min="0"
            value={value}
            onChange={(e) => setDieselPence(e.target.value)}
            placeholder="e.g. 133"
            className="mt-1 block w-48 rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={handleCheck}
          disabled={value === "" || fuelCheck.isPending}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
        >
          {fuelCheck.isPending ? "Checking…" : "Check"}
        </button>
      </div>

      {showProvenance && invoice.fuel_last_used && (
        invoice.fuel_last_used.same_week ? (
          <p className="mt-2 text-xs text-gray-500">
            Pre-filled from invoice {invoice.fuel_last_used.invoice_number} dated{" "}
            {formatDate(invoice.fuel_last_used.invoice_date)}, entered by{" "}
            {invoice.fuel_last_used.checked_by} on{" "}
            {formatDateTime(invoice.fuel_last_used.checked_at)}.
          </p>
        ) : (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <p className="font-bold">Pre-filled from a different week</p>
            <p>
              Invoice {invoice.fuel_last_used.invoice_number} dated{" "}
              {formatDate(invoice.fuel_last_used.invoice_date)} is not in this invoice's week.
              Box sends the price each Friday for the following week, so confirm this is the
              right price before you click Check.
            </p>
          </div>
        )
      )}

      {fuelCheck.isError && (
        <p className="mt-2 text-sm text-red-600">
          {fuelCheck.error instanceof ApiError
            ? fuelCheck.error.message
            : "Fuel check failed. Please try again."}
        </p>
      )}

      {invoice.fuel_checked_by && invoice.fuel_checked_at && (
        <p className="mt-3 text-xs text-gray-500">
          Checked by {invoice.fuel_checked_by} at {formatDateTime(invoice.fuel_checked_at)}
        </p>
      )}
    </div>
  );
}
